import React, { useState, useEffect, useRef, MouseEvent, useMemo } from 'react';
import cls from 'classnames';
import { l10n } from '@ott/l10n';
import styles from './ExpandableNote.scss';

const REFILL_MESSAGE_LOCATOR = 'refill-message';
const SUBSTRING_RATIO = 5.2;

type Props = { text: string };

const ExpandableNote = ({ text }: Props): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflow, setIsOverflow] = useState(false);
  const [textLen, setTextLen] = useState(0);

  const textRef = useRef<HTMLDivElement | null>(null);

  const changeTextLen = () => {
    const messageContainerWidth = textRef.current?.clientWidth;

    if (messageContainerWidth) {
      const collapsedTextLen = messageContainerWidth / SUBSTRING_RATIO;
      setIsOverflow(text.length > collapsedTextLen);
      setTextLen(collapsedTextLen);
    }
  };

  useEffect(() => {
    changeTextLen();
    window.addEventListener('resize', changeTextLen);

    return () => window.removeEventListener('resize', changeTextLen);
  }, []);

  const note = useMemo(() => {
    if (!isOverflow) return text;

    return !isExpanded ? `${text.slice(0, textLen).trim()}...` : text;
  }, [isExpanded, isOverflow, textLen]);

  const handleButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsExpanded((prev) => !prev);
  };

  return (
    <div className={cls(styles.root, styles.fadeIn)} data-locator={REFILL_MESSAGE_LOCATOR}>
      <div ref={textRef} className={styles.text}>
        <span className={cls(styles.message)} data-locator={`${REFILL_MESSAGE_LOCATOR}__text`}>
          {note}
        </span>
        {isOverflow && (
          <button
            type="button"
            className={styles.button}
            onClick={handleButtonClick}
            data-locator={`${REFILL_MESSAGE_LOCATOR}__expand-collapse`}
          >
            {isExpanded
              ? l10n('virtualCard.refillForm.collapseRefillMessage')
              : l10n('virtualCard.refillForm.expandRefillMessage')}
          </button>
        )}
      </div>
    </div>
  );
};

export default ExpandableNote;
